'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Zap, Search, User, Settings, LogOut, LogIn, Briefcase, BookOpen, Store, Workflow, Clock, Activity, LayoutGrid, ChevronDown, Menu, X, Check, Boxes } from 'lucide-react'
import { DefaultAvatar } from '@/components/ui/default-avatar'
import { ThemeToggle } from '@/components/theme/theme-toggle'
import { NotificationBell } from '@/components/notifications/notification-bell'
import { HeaderBillingBadge } from '@/components/billing/header-billing-badge'
import { useSearch } from '@/components/search/search-provider'
import { QuickCaptureTrigger } from './quick-capture-trigger'
import { signOut } from '@/app/(auth)/actions'

interface FeedHeaderProps {
  userId?: string
  username?: string
  avatarUrl?: string | null
  accountType?: string
  companySlug?: string
  /** Billing plan for the authenticated user (drives the usage badge). */
  plan?: string
  /** Count of schedules that need attention (paused / auto-paused / failing). */
  schedulesAttention?: number
}

export function FeedHeader({ userId, username, avatarUrl, accountType, companySlug, plan, schedulesAttention = 0 }: FeedHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [workspaceOpen, setWorkspaceOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const workspaceRef = useRef<HTMLDivElement>(null)
  const mobileNavRef = useRef<HTMLDivElement>(null)
  const { open: openSearch } = useSearch()
  const pathname = usePathname()

  const isActive = (href: string) => pathname?.startsWith(href)
  const isWorkspaceActive =
    isActive('/vault') || isActive('/agents') || isActive('/projects') || isActive('/pipelines') || isActive('/schedules')

  const handleSignOut = useCallback(async () => {
    setMenuOpen(false)
    await signOut()
  }, [])

  useEffect(() => {
    if (!menuOpen && !workspaceOpen && !mobileNavOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current && !menuRef.current.contains(target)) setMenuOpen(false)
      if (workspaceRef.current && !workspaceRef.current.contains(target)) setWorkspaceOpen(false)
      if (mobileNavRef.current && !mobileNavRef.current.contains(target)) setMobileNavOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen, workspaceOpen, mobileNavOpen])

  // Close mobile drawer on route change so navigation feels natural.
  useEffect(() => {
    setMobileNavOpen(false)
  }, [pathname])

  return (
    <header className="sticky top-0 z-10 border-b border-[var(--color-nav-border)] bg-[var(--color-nav-bg)] backdrop-blur-md">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6"
        aria-label="Feed navigation"
      >
        {/* Logo + Search */}
        <div className="flex flex-1 items-center gap-3 min-w-0">
          <Link
            // Logged-out visitors (and crawlers) get the public home "/" —
            // /feed is auth-gated and 307s them to /login, which Ahrefs flags
            // as a redirect link on every public page (2026-09-16). Signed-in
            // users still go straight to their feed.
            href={userId ? '/feed' : '/'}
            aria-label="PromptVan home"
            className="flex shrink-0 items-center gap-2 font-[family-name:var(--font-heading)] text-lg font-bold text-[var(--color-nav-text)] transition-colors hover:text-[var(--color-brand-indigo)]"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--color-brand-indigo)] to-[var(--color-brand-violet)]">
              {/* PromptVan "V" mark — matches the favicon (app/icon.svg). Was a
                  Zap/lightning icon, which is not the brand logo. */}
              <svg viewBox="0 0 512 512" className="h-5 w-5" fill="none" aria-hidden="true">
                <path
                  d="M150 150 L256 368 L362 150"
                  stroke="#ffffff"
                  strokeWidth="76"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="hidden sm:inline">PromptVan</span>
          </Link>

          {/*
           * Inline search bar.
           *
           * min-w-0 is load-bearing: lets the button shrink below its
           * intrinsic content width so the inner truncate span actually
           * clips. Without it, flex items default to min-width: auto
           * (= content width), and the button overflows its
           * `flex-1 min-w-0` parent into the right-side dropdown column.
           * Verified on staging 2026-05-04: search at x=175-435 overlapped
           * Workspace at x=334-474 by 101px.
           */}
          {username && (
            <button
              type="button"
              onClick={openSearch}
              aria-label="Search prompts, people, jobs"
              className="flex h-9 w-full min-w-0 max-w-md items-center gap-2 rounded-full border border-[var(--color-nav-border)] bg-white/5 px-3.5 text-sm text-[var(--color-nav-text)]/70 transition-colors hover:bg-white/10 hover:text-[var(--color-nav-text)]"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="truncate">Search prompts, people, jobs…</span>
              {/* opacity bumped 60→90 to clear WCAG AA contrast (axe 2026-09-17) */}
              <kbd className="ml-auto hidden shrink-0 items-center gap-0.5 rounded border border-[var(--color-nav-border)] px-1.5 py-0.5 text-xs font-medium opacity-90 lg:inline-flex">
                ⌘K
              </kbd>
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {username && accountType !== 'company' && (
            <div className="relative hidden sm:block" ref={workspaceRef}>
              <button
                type="button"
                onClick={() => setWorkspaceOpen((o) => !o)}
                aria-expanded={workspaceOpen}
                aria-haspopup="menu"
                aria-label="Workspace"
                title="Workspace"
                className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-[colors,transform,opacity] duration-100 hover:bg-white/10 hover:text-[var(--color-nav-text)] ${isWorkspaceActive ? 'bg-white/10 text-[var(--color-nav-text)]' : 'text-[var(--color-nav-text)]/60'}`}
              >
                <LayoutGrid className="h-[16px] w-[16px]" />
                {/* Text labels only ≥lg: at 640–1024px the full set of labeled
                    nav items overflows and pushes the group over the logo
                    (measured 32px overlap at 844px on staging 2026-07-04). */}
                <span className="hidden lg:inline">Workspace</span>
                {schedulesAttention > 0 && (
                  <span
                    aria-label={`${schedulesAttention} schedule${schedulesAttention === 1 ? '' : 's'} needs attention`}
                    title={`${schedulesAttention} schedule${schedulesAttention === 1 ? '' : 's'} needs attention`}
                    className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-semibold"
                    style={{ background: 'var(--color-semantic-error)', color: 'white' }}
                  >
                    {schedulesAttention > 9 ? '9+' : schedulesAttention}
                  </span>
                )}
                <ChevronDown className="hidden h-3 w-3 opacity-60 lg:inline" />
              </button>
              {workspaceOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-20 mt-2 w-72 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-1.5 shadow-[var(--shadow-md)]"
                >
                  {/* Vault — the subset of your library connected to your AI via MCP & CLI.
                      Sits at the top with a distinct icon so it doesn't read as a duplicate
                      of the catalog items below. */}
                  <Link
                    href="/vault"
                    onClick={() => setWorkspaceOpen(false)}
                    aria-current={isActive('/vault') ? 'page' : undefined}
                    className={`flex w-full items-start gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-left hover:bg-[var(--color-bg-elevated)] ${isActive('/vault') ? 'bg-[var(--color-bg-elevated)]' : ''}`}
                  >
                    <Boxes className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isActive('/vault') ? 'text-[var(--color-brand-indigo)]' : 'text-[var(--color-text-muted)]'}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${isActive('/vault') ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}>Vault</span>
                      <span className="block text-xs text-[var(--color-text-muted)]">Connected to your AI · via MCP &amp; CLI</span>
                    </span>
                    {isActive('/vault') && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--color-brand-indigo)]" />}
                  </Link>

                  {/* Library group removed from the dropdown — agents / prompts / projects /
                      imports now live in the Vault page as filterable type chips. */}

                  <p className="px-2.5 pb-1 pt-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">Automation</p>
                  <Link
                    href="/pipelines"
                    onClick={() => setWorkspaceOpen(false)}
                    aria-current={isActive('/pipelines') ? 'page' : undefined}
                    className={`flex w-full items-start gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-left hover:bg-[var(--color-bg-elevated)] ${isActive('/pipelines') ? 'bg-[var(--color-bg-elevated)]' : ''}`}
                  >
                    <Workflow className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isActive('/pipelines') ? 'text-[var(--color-brand-indigo)]' : 'text-[var(--color-text-muted)]'}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${isActive('/pipelines') ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}>Pipelines</span>
                      <span className="block text-xs text-[var(--color-text-muted)]">Chain agents into workflows</span>
                    </span>
                    {isActive('/pipelines') && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--color-brand-indigo)]" />}
                  </Link>
                  <Link
                    href="/schedules"
                    onClick={() => setWorkspaceOpen(false)}
                    aria-current={isActive('/schedules') ? 'page' : undefined}
                    className={`flex w-full items-start gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-left hover:bg-[var(--color-bg-elevated)] ${isActive('/schedules') ? 'bg-[var(--color-bg-elevated)]' : ''}`}
                  >
                    <Clock className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isActive('/schedules') ? 'text-[var(--color-brand-indigo)]' : 'text-[var(--color-text-muted)]'}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${isActive('/schedules') ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}>Schedules</span>
                      <span className="block text-xs text-[var(--color-text-muted)]">Run on a timer</span>
                    </span>
                    {schedulesAttention > 0 && (
                      <span
                        aria-label={`${schedulesAttention} schedule${schedulesAttention === 1 ? '' : 's'} needs attention`}
                        title={`${schedulesAttention} schedule${schedulesAttention === 1 ? '' : 's'} needs attention`}
                        className="mt-0.5 inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-xs font-semibold"
                        style={{ background: 'var(--color-semantic-error)', color: 'white' }}
                      >
                        {schedulesAttention > 9 ? '9+' : schedulesAttention}
                      </span>
                    )}
                  </Link>

                  <Link
                    href="/runs"
                    onClick={() => setWorkspaceOpen(false)}
                    aria-current={isActive('/runs') ? 'page' : undefined}
                    className={`flex w-full items-start gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-left hover:bg-[var(--color-bg-elevated)] ${isActive('/runs') ? 'bg-[var(--color-bg-elevated)]' : ''}`}
                  >
                    <Activity className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isActive('/runs') ? 'text-[var(--color-brand-indigo)]' : 'text-[var(--color-text-muted)]'}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${isActive('/runs') ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}>Runs</span>
                      <span className="block text-xs text-[var(--color-text-muted)]">Every run &amp; its cost</span>
                    </span>
                  </Link>

                  <div className="my-1.5 h-px bg-[var(--color-border-default)]" />
                  <Link
                    href="/docs"
                    onClick={() => setWorkspaceOpen(false)}
                    className="flex w-full items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                  >
                    <BookOpen className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                    All docs
                  </Link>
                </div>
              )}
            </div>
          )}
          {/* Jobs nav link soft-removed per request (hidden from navbar).
          {username && (
            <Link
              href="/jobs"
              aria-label="Jobs"
              title="Jobs"
              className={`hidden h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-[colors,transform,opacity] duration-100 hover:bg-white/10 hover:text-[var(--color-nav-text)] sm:flex ${isActive('/jobs') ? 'bg-white/10 text-[var(--color-nav-text)]' : 'text-[var(--color-nav-text)]/60'}`}
            >
              <Briefcase className="h-[16px] w-[16px]" />
              <span className="hidden lg:inline">Jobs</span>
            </Link>
          )}
          */}
          {/* Marketplace nav link soft-removed pending product re-enable.
              See src/app/(app)/marketplace/page.tsx for re-enable steps.
          {username && (
            <Link
              href="/marketplace"
              className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-[colors,transform,opacity] duration-100 hover:bg-white/10 hover:text-[var(--color-nav-text)] ${isActive('/marketplace') ? 'bg-white/10 text-[var(--color-nav-text)]' : 'text-[var(--color-nav-text)]/60'}`}
            >
              <Store className="h-[16px] w-[16px]" />
              <span className="hidden sm:inline">Marketplace</span>
            </Link>
          )}
          */}
          {username && (
            <Link
              href="/signal"
              aria-label="The Wire"
              title="The Wire"
              className={`hidden h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-[colors,transform,opacity] duration-100 hover:bg-white/10 hover:text-[var(--color-nav-text)] sm:flex ${isActive('/signal') ? 'bg-white/10 text-[var(--color-nav-text)]' : 'text-[var(--color-nav-text)]/60'}`}
            >
              <Zap className="h-[16px] w-[16px]" />
              <span className="hidden lg:inline">The Wire</span>
            </Link>
          )}
          {userId && accountType !== 'company' && (
            <span className="hidden lg:inline-flex">
              <HeaderBillingBadge plan={plan} />
            </span>
          )}
          {userId && (
            <span className="hidden sm:inline-flex">
              <QuickCaptureTrigger />
            </span>
          )}
          {userId && <NotificationBell userId={userId} />}
          <span className="hidden sm:inline-flex">
            <ThemeToggle />
          </span>

          {/* Mobile-only hamburger that exposes the nav surfaces hidden
              above (Workspace, Jobs, Signal, ThemeToggle). Shown only on
              <sm so it doesn't clutter the desktop bar. */}
          {username && (
            <div className="relative sm:hidden" ref={mobileNavRef}>
              <button
                type="button"
                onClick={() => setMobileNavOpen((o) => !o)}
                aria-expanded={mobileNavOpen}
                aria-haspopup="menu"
                aria-label="Open navigation"
                className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--color-nav-text)]/60 transition-colors hover:bg-white/10 hover:text-[var(--color-nav-text)]"
              >
                {mobileNavOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
                {schedulesAttention > 0 && !mobileNavOpen && (
                  <span
                    aria-hidden="true"
                    className="absolute right-1 top-1 inline-flex h-2 w-2 rounded-full"
                    style={{ background: 'var(--color-semantic-error)' }}
                  />
                )}
              </button>
              {mobileNavOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-20 mt-2 w-60 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] py-2 shadow-[var(--shadow-md)]"
                >
                  {accountType !== 'company' && (
                    <>
                      <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                        Workspace
                      </p>
                      <Link
                        href="/vault"
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                      >
                        <Boxes className="h-3.5 w-3.5" />
                        Vault
                      </Link>
                      {/* Library group removed — lives in the Vault page as type chips. */}
                      <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                        Automation
                      </p>
                      <Link
                        href="/pipelines"
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                      >
                        <Workflow className="h-3.5 w-3.5" />
                        Pipelines
                      </Link>
                      <Link
                        href="/schedules"
                        className="flex w-full items-center justify-between gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                      >
                        <span className="flex items-center gap-2.5">
                          <Clock className="h-3.5 w-3.5" />
                          Schedules
                        </span>
                        {schedulesAttention > 0 && (
                          <span
                            aria-label={`${schedulesAttention} schedule${schedulesAttention === 1 ? '' : 's'} needs attention`}
                            title={`${schedulesAttention} schedule${schedulesAttention === 1 ? '' : 's'} needs attention`}
                            className="inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-semibold"
                            style={{ background: 'var(--color-semantic-error)', color: 'white' }}
                          >
                            {schedulesAttention > 9 ? '9+' : schedulesAttention}
                          </span>
                        )}
                      </Link>
                      <div className="my-1 h-px bg-[var(--color-border-default)]" />
                    </>
                  )}
                  {/* Jobs nav link soft-removed per request (hidden from navbar).
                  <Link
                    href="/jobs"
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                  >
                    <Briefcase className="h-3.5 w-3.5" />
                    Jobs
                  </Link>
                  */}
                  <Link
                    href="/signal"
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Signal
                  </Link>
                  <div className="my-1 h-px bg-[var(--color-border-default)]" />
                  <Link
                    href="/docs"
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                  >
                    <BookOpen className="h-3.5 w-3.5" />
                    All docs
                  </Link>
                  <div className="my-1 h-px bg-[var(--color-border-default)]" />
                  <div className="flex items-center justify-between px-3 py-2 text-xs text-[var(--color-text-muted)]">
                    <span>Theme</span>
                    <ThemeToggle />
                  </div>
                </div>
              )}
            </div>
          )}

          {username ? (
            /* User avatar dropdown */
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[var(--color-nav-border)] transition-colors hover:border-[var(--color-brand-indigo)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
                aria-label="User menu"
                aria-expanded={menuOpen}
              >
                {avatarUrl ? (
                  <Image src={avatarUrl} alt="" width={36} height={36} className="h-full w-full object-cover" />
                ) : (
                  <DefaultAvatar name={username} size={36} />
                )}
              </button>

              {menuOpen && (
                  <div
                    className="absolute right-0 top-full z-20 mt-2 w-48 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] py-1 shadow-[var(--shadow-md)]"
                  >
                    <Link
                      href={accountType === 'company' && companySlug ? `/company/${companySlug}` : `/u/${username}`}
                      onClick={() => setMenuOpen(false)}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                    >
                      <User className="h-3.5 w-3.5" />
                      View Profile
                    </Link>
                    <Link
                      href="/settings/account"
                      onClick={() => setMenuOpen(false)}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Settings
                    </Link>
                    <Link
                      href="/blog"
                      onClick={() => setMenuOpen(false)}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                    >
                      <BookOpen className="h-3.5 w-3.5" />
                      Blog
                    </Link>
                    <div className="my-1 border-t border-[var(--color-border-default)]" />
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-400 hover:bg-[var(--color-bg-elevated)]"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      Sign Out
                    </button>
                  </div>
              )}
            </div>
          ) : (
            <Link
              href="/login"
              className="flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-[var(--color-nav-text)]/60 transition-[colors,transform,opacity] duration-100 hover:bg-white/10 hover:text-[var(--color-nav-text)]"
            >
              <LogIn className="h-[16px] w-[16px]" />
              <span className="hidden sm:inline">Sign In</span>
            </Link>
          )}
        </div>
      </nav>
    </header>
  )
}
